import { ArrowDownToLine, RefreshCw, User, X } from 'lucide-react';
import type { MiroColorRule } from '../../../../lib/fieldMappingRegistry';
import { MappingSetNotice } from '../../connectors/MappingSetNotice';
import { MiroIcon } from './IntegrationIcons';
import type { MiroConnection } from './useMiroConnection';

const COLOR_TO_CATEGORY: Record<string, string> = {
  red: 'urgent',
  orange: 'high_priority',
  yellow: 'idea',
  green: 'approved',
  blue: 'technical',
  pink: 'feedback',
  violet: 'enhancement',
};

export function MiroImportModal({
  miro,
  workspaceId,
  itemColorRules,
}: {
  miro: MiroConnection;
  workspaceId: string | null;
  itemColorRules: MiroColorRule[];
}) {
  const {
    showImportMiro,
    setShowImportMiro,
    useTemplateMode,
    setUseTemplateMode,
    selectedMiroTemplate,
    setSelectedMiroTemplate,
    selectedMiroBoard,
    setSelectedMiroBoard,
    miroTemplates,
    miroBoards,
    miroItems,
    selectedMiroItems,
    templateImportPreview,
    isLoading,
    loadTemplateImportPreview,
    loadMiroPreview,
    selectAllMiroItems,
    toggleMiroSelection,
    handleTemplateImport,
    handleMiroImport,
  } = miro;

  return (
    <>
      {/* Import from Miro Modal */}
      {showImportMiro && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--bg-raised)] shadow-xl max-w-3xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-[var(--rule)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[var(--mustard-dim)]">
                  <MiroIcon />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text-pri)]">Import from Miro</h2>
                  <p className="text-sm text-[var(--text-ter)]">
                    {useTemplateMode
                      ? 'Extract item metadata from spatial zone placement'
                      : 'Select sticky notes — colors and other signals set item metadata'}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowImportMiro(false)} className="p-2 hover:bg-[var(--hover-tint)]">
                <X className="h-5 w-5 text-[var(--text-ter)]" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {/* Import Mode Toggle */}
              <div className="mb-4 flex items-center gap-4 p-3 bg-[var(--surface)]">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={useTemplateMode}
                    onChange={() => setUseTemplateMode(true)}
                    className="text-[var(--mustard)] focus:ring-yellow-500"
                  />
                  <span className="text-sm font-medium">Template Mode</span>
                  <span className="text-xs text-[var(--text-ter)]">(Metadata from spatial zones)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!useTemplateMode}
                    onChange={() => setUseTemplateMode(false)}
                    className="text-[var(--mustard)] focus:ring-yellow-500"
                  />
                  <span className="text-sm font-medium">Simple Mode</span>
                  <span className="text-xs text-[var(--text-ter)]">(Color and signal-based metadata)</span>
                </label>
              </div>

              {/* Template Selection (Template Mode) */}
              {useTemplateMode && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                    Prioritization Template
                  </label>
                  <select
                    value={selectedMiroTemplate}
                    onChange={(e) => {
                      setSelectedMiroTemplate(e.target.value);
                      // If board is already selected, reload preview
                      if (selectedMiroBoard && e.target.value) {
                        loadTemplateImportPreview(selectedMiroBoard, e.target.value);
                      }
                    }}
                    className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="">Choose a template...</option>
                    {miroTemplates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.zoneCount} zones)
                      </option>
                    ))}
                  </select>
                  {selectedMiroTemplate && (
                    <div className="mt-2 text-xs text-[var(--text-ter)]">
                      {miroTemplates.find(t => t.id === selectedMiroTemplate)?.description}
                    </div>
                  )}
                </div>
              )}

              {/* Board Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Select Board</label>
                <select
                  value={selectedMiroBoard}
                  onChange={(e) => {
                    setSelectedMiroBoard(e.target.value);
                    if (e.target.value) {
                      if (useTemplateMode && selectedMiroTemplate) {
                        loadTemplateImportPreview(e.target.value, selectedMiroTemplate);
                      } else {
                        loadMiroPreview(e.target.value);
                      }
                    }
                  }}
                  className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">Choose a board...</option>
                  {miroBoards.map(board => (
                    <option key={board.id} value={board.id}>{board.name}</option>
                  ))}
                </select>
              </div>

              <MappingSetNotice connectorType="miro" workspaceId={workspaceId} className="mb-4" />

              {/* Loading State */}
              {isLoading && (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin text-[var(--mustard)]" />
                  <span className="ml-2 text-[var(--text-sec)]">Loading board...</span>
                </div>
              )}

              {/* Template Preview (Zone Summary) */}
              {useTemplateMode && !isLoading && templateImportPreview && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-[var(--text-sec)] mb-3">Zone Distribution</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {templateImportPreview.zoneSummary.map(zone => (
                      <div
                        key={zone.zoneId}
                        className="p-3 border border-[var(--rule)] bg-[var(--surface)]"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-[var(--text-pri)]">{zone.zoneName}</span>
                          <span className="text-lg font-bold text-[var(--mustard)]">{zone.itemCount}</span>
                        </div>
                        <div className="text-xs text-[var(--text-ter)]">
                          {Object.entries(zone.metadata || {}).slice(0, 2).map(([key, value]) => (
                            <span key={key} className="mr-2">
                              {key}: {String(value)}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-sm text-[var(--text-sec)]">
                    Total: <strong>{templateImportPreview.totalItems}</strong> items will be imported with zone-based metadata
                  </div>
                  {templateImportPreview.sampleItems && templateImportPreview.sampleItems.length > 0 && (
                    <div className="mt-3">
                      <h4 className="text-xs font-medium text-[var(--text-ter)] mb-2">Sample Items</h4>
                      <div className="space-y-1">
                        {templateImportPreview.sampleItems.slice(0, 5).map(item => (
                          <div key={item.id} className="text-xs flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded ${
                              item.confidence === 'exact' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
                              item.confidence === 'overlap' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' :
                              'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                            }`}>
                              {item.zone}
                            </span>
                            <span className="text-[var(--text-sec)] truncate">{item.content}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Simple Mode: Sticky Notes List */}
              {!useTemplateMode && !isLoading && miroItems.length > 0 && (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-[var(--text-sec)]">{miroItems.length} items found</span>
                    <button
                      onClick={selectAllMiroItems}
                      className="text-sm text-[var(--mustard)] hover:text-[var(--mustard)] font-medium"
                    >
                      Select All
                    </button>
                  </div>
                  {itemColorRules.length === 0 && (
                    <p className="mb-3 text-xs text-[var(--mustard)] bg-[var(--mustard-dim)] border border-[var(--mustard)] px-3 py-2">
                      No color rules configured for items. Go to{' '}
                      <strong>Settings → Connectors → Miro → Color Mappings</strong> to map
                      sticky note colors to item fields (altitude, category, etc.).
                    </p>
                  )}
                  <div className="space-y-2">
                    {miroItems.map(item => (
                      <label
                        key={item.id}
                        className={`flex items-start gap-3 p-3  border cursor-pointer transition-colors ${
                          selectedMiroItems.includes(item.id)
                            ? 'border-[var(--mustard)] bg-[var(--mustard-dim)]'
                            : 'border-[var(--rule)] hover:bg-[var(--surface)]'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedMiroItems.includes(item.id)}
                          onChange={() => toggleMiroSelection(item.id)}
                          className="mt-1 rounded border-[var(--rule)] text-[var(--mustard)] focus:ring-yellow-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="font-medium text-[var(--text-pri)]">{item.content}</span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-[var(--text-ter)]">
                            {item.author && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {item.author}
                              </span>
                            )}
                          </div>
                        </div>
                        {(() => {
                          // Show user-configured rule label if available; otherwise fall back to defaults
                          const rule = itemColorRules.find(r =>
                            r.color.toLowerCase().trim() === (item.color || '').toLowerCase().trim()
                          );
                          const badge = rule
                            ? `${rule.field}: ${rule.value ?? rule.label ?? '—'}`
                            : (item.suggestedCategory || COLOR_TO_CATEGORY[item.color] || 'idea');
                          return (
                            <span className={`text-xs px-2 py-1 rounded ${
                              rule
                                ? 'bg-primary-50 text-primary-700 border border-primary-200'
                                : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                            }`}>
                              {badge}
                            </span>
                          );
                        })()}
                      </label>
                    ))}
                  </div>
                </>
              )}

              {/* No items message */}
              {!isLoading && selectedMiroBoard && !useTemplateMode && miroItems.length === 0 && (
                <div className="text-center py-8 text-[var(--text-ter)]">
                  No sticky notes found in this board
                </div>
              )}

              {!isLoading && selectedMiroBoard && useTemplateMode && !templateImportPreview && (
                <div className="text-center py-8 text-[var(--text-ter)]">
                  {!selectedMiroTemplate 
                    ? 'Select a template to preview zone detection'
                    : 'Loading preview...'}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-[var(--rule)] flex items-center justify-between bg-[var(--surface)]">
              <span className="text-sm text-[var(--text-sec)]">
                {useTemplateMode
                  ? templateImportPreview
                    ? `${templateImportPreview.totalItems} items across ${templateImportPreview.zoneSummary.filter(z => z.itemCount > 0).length} zones`
                    : 'Select board and template'
                  : `${selectedMiroItems.length} item(s) selected`
                }
              </span>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowImportMiro(false)}
                  className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={useTemplateMode ? handleTemplateImport : handleMiroImport}
                  disabled={
                    useTemplateMode
                      ? !selectedMiroBoard || !selectedMiroTemplate || !templateImportPreview || isLoading
                      : selectedMiroItems.length === 0 || isLoading
                  }
                  className="px-4 py-2 bg-[var(--mustard)] hover:bg-[var(--mustard)] text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLoading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowDownToLine className="h-4 w-4" />
                  )}
                  {useTemplateMode ? 'Import with Zones' : 'Import Selected'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
